import { useMemo } from 'react';
import moment from 'moment';
import {
  IMetricValue,
  TGamesMetricData,
  TMetricValueList,
} from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList';
import { getIfPercentage } from '@intelligence/utils/common';
import { IMetricsOptionItem } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import {
  fillInEmptyData,
  DateTypePrefixMap,
} from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList/hooks/utils';

interface IMetricGameDataItem {
  metric: string;
  text: string;
  icon?: string;
  dataSource: string;
  isPercentage: boolean;
  colors?: string[];
  isSentinel?: boolean;
  gamesData: {
    id: string;
    name: string;
    cover: string;
    history: {
      time: string;
      value: number;
    }[];
  }[];
}

export type TMetricGameData = IMetricGameDataItem[];

interface IMetricGameDataProps {
  // 所有游戏指标按日期分组全量数据
  gamesMetricData: TGamesMetricData;
  // 游戏指标聚合数据，表格展示数据，用于给指标对应的游戏数据排序
  gamesMetricDataAggregation: TGamesMetricData;
  // 查询的指标
  fetchParamsMetrics: IMetricsOptionItem[];
  // 时间粒度
  dateType: 'daily' | 'weekly' | 'monthly';
  // 是否 alignToRelease
  isAlignToRelease?: boolean;
  // 返回的查询时间，用于空数据补全
  responseQueryDate?: { startDate: string | number; endDate: string | number };
  // 筛选的游戏列表，用于处理表格删除后，过滤掉可视化的对应游戏数据
  compareGameIds: string[];
  // 游戏对应颜色 map
  gamesColorMap?: { [key: string]: string };
}

function useMetricGameData({
  gamesMetricData,
  gamesMetricDataAggregation,
  fetchParamsMetrics = [],
  dateType,
  isAlignToRelease,
  responseQueryDate,
  compareGameIds,
  gamesColorMap,
}: IMetricGameDataProps): TMetricGameData {
  return useMemo<TMetricGameData>(() => {
    if (!gamesMetricData?.length) return [];

    // 最终数据按照所有查询指标的配置数据做为模板
    const formattedData: TMetricGameData = fetchParamsMetrics.map((item) => ({
      metric: item.value,
      text: item.text,
      dataSource: item.dataSource,
      icon: item.icon,
      isPercentage: getIfPercentage(item.value),
      gamesData: [],
    }));

    for (const currentGameData of gamesMetricData) {
      const metricsData = currentGameData?.metric_value_list;

      const metricsDataMap: { [key: string]: TMetricValueList } = {};

      // 将当前游戏数据按照不同 metric 指标进行拆分，生成 {metric:[metricData]} 的 map 数据结构
      metricsData?.forEach((item) => {
        // release 数据日期项拼接粒度前缀
        const currentItem = isAlignToRelease
          ? {
              ...item,
              date: `${DateTypePrefixMap[dateType as string]}${item.date}`,
            }
          : item;
        if (metricsDataMap[item.metric]) {
          metricsDataMap[item.metric].push(currentItem);
        } else {
          metricsDataMap[item.metric] = [currentItem];
        }
      });

      // 遍历当前游戏中的所有 metric 指标
      const metrics = Object.keys(metricsDataMap);

      metrics.forEach((metric) => {
        const currentMetricData = metricsDataMap[metric];

        // 将当前 metric 指标对应数据按照时间排序
        if (isAlignToRelease) {
          // alignToRelease 数据按照日期排序
          currentMetricData.sort((a, b) => {
            const aDateNum = a.date?.replace('D', '')?.replace('W', '')?.replace('M', '');
            const bDateNum = b.date?.replace('D', '')?.replace('W', '')?.replace('M', '');

            return Number(aDateNum) - Number(bDateNum);
          });
        } else {
          // 数据按照日期排序
          currentMetricData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
        }

        const isPercentage = getIfPercentage(metric);

        // 跳过删除的数据
        if (!compareGameIds?.includes(currentGameData.id)) return;

        const data = fillInEmptyData({
          currentMetricData,
          responseQueryDate,
          dateType,
          isAlignToRelease,
        });

        const gameData = {
          id: currentGameData.id,
          name: currentGameData.name,
          cover: currentGameData.cover,
          history: data?.map((item) => {
            // 根据是否 alignToRelease 处理 x 轴时间数据
            let time;
            if (isAlignToRelease) {
              time = item.date;
            } else {
              // 日期根据时间粒度 dateType 处理
              time = dateType?.toLocaleLowerCase()?.includes('month')
                ? moment(item.date).format('YYYY-MM')
                : moment(item.date).format('YYYY-MM-DD');
            }

            // 值按照是否百分比处理
            const value = isPercentage ? item.value * 100 : item.value;

            return {
              time,
              value,
            };
          }),
        };

        // 将当前游戏的指标放入最终的所有指标数据中
        const currentMetricFinalData = formattedData.find(
          (item: IMetricGameDataItem) => item.metric === metric,
        );
        if (currentMetricFinalData) {
          currentMetricFinalData.gamesData.push(gameData);
        }
      });
    }

    // 排序结构 Map
    // const mapMockData = {
    //   metric1: {
    //     game1: 0,
    //     game2: 1,
    //   },
    // };

    // 获取每个指标对应游戏的顺序
    const metricsGamesDataOrderMap: { [key: string]: any } = {};

    for (const currentAggGameData of gamesMetricDataAggregation) {
      const metricsData = currentAggGameData?.metric_value_list;

      // 将当前游戏数据按照不同 metric 指标进行拆分，生成 {metric:[metricData]} 的 map 数据结构
      metricsData?.forEach((item) => {
        if (metricsGamesDataOrderMap[item.metric]) {
          metricsGamesDataOrderMap[item.metric].push(item);
        } else {
          metricsGamesDataOrderMap[item.metric] = [item];
        }
      });
    }

    const metrics = Object.keys(metricsGamesDataOrderMap);
    metrics.forEach((metric) => {
      metricsGamesDataOrderMap[metric].sort(
        (a: IMetricValue, b: IMetricValue) => b.value - a.value,
      );
      metricsGamesDataOrderMap[metric] = metricsGamesDataOrderMap[metric]?.reduce(
        (prev: { [key: string]: number }, curr: IMetricValue, index: number) => {
          return {
            ...prev,
            [curr.id]: index,
          };
        },
        {},
      );
    });

    // 排序：无数据卡片放到最后
    formattedData.sort((a, b) => {
      if (!a.gamesData?.length && !b.gamesData?.length) return 0; // 都为空，保持原顺序
      if (!a.gamesData?.length) return 1; // a 为空，放后面
      if (!b.gamesData?.length) return -1; // b 为空，放后面
      return 0; // 其他情况保持原顺序
    });

    // 给每个指标对应的游戏列表数据排序，根据游戏根据当前指标的降序，并获取对应游戏色盘
    formattedData.forEach((item) => {
      const currentGameOrderMap = metricsGamesDataOrderMap[item.metric];
      currentGameOrderMap &&
        item.gamesData.sort((a, b) => currentGameOrderMap[a.id] - currentGameOrderMap[b.id]);

      // 当前数据项游戏列表对应色盘获取
      if (gamesColorMap) {
        const colors = item.gamesData.map((game) => gamesColorMap[game.id]);
        // eslint-disable-next-line no-param-reassign
        item.colors = colors;
      }
    });

    return formattedData;
  }, [
    dateType,
    gamesMetricData,
    gamesMetricDataAggregation,
    fetchParamsMetrics,
    isAlignToRelease,
    responseQueryDate,
    compareGameIds,
    gamesColorMap,
  ]);
}

export default useMetricGameData;
